/**
 * 🎬 Video Queue
 * 動画の順序管理とランダム選択
 *
 * 責任:
 * - 動画リストの管理
 * - ランダム選択ロジック
 * - 再生履歴の管理
 */

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Video category types
 */
export type VideoCategory = 'all' | 'flat' | 'depth' | 'withdepth';
import { logger } from '@/utils/logger.js';
import { useTokchanStore, type UnifiedCatalogCache } from '@/store/tokchanStore.js';

/**
 * 統合カタログキャッシュを取得するヘルパー関数
 * Zustand store優先、windowフォールバック
 */
const getUnifiedCatalogCache = (): UnifiedCatalogCache | null => {
  return useTokchanStore.getState().getUnifiedCatalogCache() || window.unifiedCatalogCache || null;
};

/**
 * Queue mode types
 */
export type QueueMode = 'random' | 'sequential';

/**
 * Base video data structure
 */
export interface VideoData {
    src: string;
    name: string;
    category: VideoCategory;
    metadata?: Record<string, unknown>;
}

/**
 * Queued video with priority and timestamp
 */
export interface QueuedVideo extends VideoData {
    priority?: number;
    timestamp?: number;
    playedAt?: number;
}

/**
 * Options for getting next video
 */
export interface GetNextOptions {
    category?: VideoCategory;
    avoidLast?: boolean;
    avoidHistory?: number;
}

/**
 * History entry
 */
export interface HistoryEntry extends VideoData {
    playedAt: number;
}

/**
 * Queue options
 */
export interface QueueOptions {
    maxSize?: number;
    priorityLevels?: number;
    autoRemove?: boolean;
    maxHistorySize?: number;
    mode?: QueueMode;
}

/**
 * Queue statistics
 */
export interface QueueStats {
    totalPlays: number;
    uniquePlays: number;
    mostPlayed: Array<{
        src: string;
        count: number;
    }>;
}

/**
 * Queue status information
 */
export interface QueueStatus {
    totalVideos: number;
    categoryCounts: {
        all: number;
        flat: number;
        depth: number;
    };
    mode: QueueMode;
    historySize: number;
    stats: QueueStats;
}

/**
 * Categorized videos structure
 */
export interface CategorizedVideos {
    flat: VideoData[];
    depth: VideoData[];
    all: VideoData[];
}

/**
 * Unified asset catalog structure
 */
export interface UnifiedAssetCatalog {
    backgrounds?: {
        flat_backgrounds?: {
            basePath?: string;
            assets: Array<{
                file: string;
                name?: string;
                [key: string]: unknown;
            }>;
        };
        depth_backgrounds?: {
            basePath?: string;
            assets: Array<{
                file: string;
                name?: string;
                [key: string]: unknown;
            }>;
        };
    };
}

/**
 * Window interface extension for global catalog access
 */
declare global {
    interface Window {
        VideoQueue?: typeof VideoQueue;
    }
}

// ============================================================================
// VideoQueue Class
// ============================================================================

/**
 * VideoQueue manages video playback order and selection
 */
export class VideoQueue {
    private videos: VideoData[];
    private currentIndex: number;
    private history: HistoryEntry[];
    private maxHistorySize: number;
    private mode: QueueMode;
    private categorizedVideos: CategorizedVideos;

    /**
     * Create a new VideoQueue
     * @param videos - Initial video list
     * @param options - Queue options
     */
    constructor(videos: VideoData[] = [], _options) {
        this.videos = [...videos];
        this.currentIndex = 0;
        this.history = [];
        this.maxHistorySize = _options.maxHistorySize ?? 10;
        this.mode = _options.mode ?? 'random';

        // カテゴリ別のキャッシュ
        this.categorizedVideos = this.categorizeVideos();
    }

    /**
     * 動画をカテゴリ別に分類
     */
    private categorizeVideos(): CategorizedVideos {
        const categories: CategorizedVideos = {
            flat: [],
            depth: [],
            all: [...this.videos]
        };

        this.videos.forEach(video => {
            if (video.category === 'flat') {
                categories.flat.push(video);
            } else if (video.category === 'depth' || video.category === 'withdepth') {
                categories.depth.push(video);
            }
        });

        return categories;
    }

    /**
     * 次の動画を取得
     * @param options - Selection options
     * @returns Selected video with timestamp
     */
    public getNext(options: GetNextOptions = {}): QueuedVideo {
        const {
            category = 'all',      // 'all' | 'flat' | 'depth'
            avoidLast = true,      // 直前の動画を避ける
            avoidHistory = 3       // 履歴から何個分を避けるか
        } = options;

        let candidates = this.getCandidates(category);

        if (candidates.length === 0) {
            throw new Error(`No videos available in category: ${category}`);
        }

        // 履歴から除外する動画を特定
        if (avoidLast && this.history.length > 0) {
            const recentVideos = this.history.slice(-avoidHistory).map(h => h.src);
            candidates = candidates.filter(video => !recentVideos.includes(video.src));

            // 候補がなくなった場合は制限を緩める
            if (candidates.length === 0) {
                candidates = this.getCandidates(category);
            }
        }

        // 選択
        let selectedVideo: VideoData;
        if (this.mode === 'random') {
            selectedVideo = this.selectRandom(candidates);
        } else {
            selectedVideo = this.selectSequential(candidates);
        }

        // 履歴に追加
        this.addToHistory(selectedVideo);

        return {
            ...selectedVideo,
            timestamp: Date.now()
        };
    }

    /**
     * カテゴリに基づいて候補を取得
     * @param category - Video category
     * @returns Array of candidate videos
     */
    private getCandidates(category: VideoCategory): VideoData[] {
        return this.categorizedVideos[category] || this.categorizedVideos.all;
    }

    /**
     * ランダム選択
     * @param candidates - Array of candidate videos
     * @returns Randomly selected video
     */
    private selectRandom(candidates: VideoData[]): VideoData {
        const index = Math.floor(Math.random() * candidates.length);
        const selected = candidates[index];
        if (!selected) {
            throw new Error('Failed to select random video');
        }
        return selected;
    }

    /**
     * シーケンシャル選択
     * @param candidates - Array of candidate videos
     * @returns Sequentially selected video
     */
    private selectSequential(candidates: VideoData[]): VideoData {
        this.currentIndex = (this.currentIndex + 1) % candidates.length;
        const selected = candidates[this.currentIndex];
        if (!selected) {
            throw new Error('Failed to select sequential video');
        }
        return selected;
    }

    /**
     * 履歴に追加
     * @param video - Video to add to history
     */
    private addToHistory(video: VideoData): void {
        this.history.push({
            ...video,
            playedAt: Date.now()
        });

        // 履歴サイズを制限
        if (this.history.length > this.maxHistorySize) {
            this.history.shift();
        }
    }

    /**
     * 動画を追加
     * @param video - Video to add
     */
    public addVideo(video: VideoData): void {
        this.videos.push(video);
        this.categorizedVideos = this.categorizeVideos();
    }

    /**
     * 動画を削除
     * @param src - Video source URL to remove
     */
    public removeVideo(src: string): void {
        this.videos = this.videos.filter(v => v.src !== src);
        this.categorizedVideos = this.categorizeVideos();
    }

    /**
     * 動画リストを更新
     * @param videos - New video list
     */
    public updateVideos(videos: VideoData[]): void {
        this.videos = [...videos];
        this.categorizedVideos = this.categorizeVideos();
        this.currentIndex = 0;
    }

    /**
     * モードを設定
     * @param mode - Queue mode to set
     */
    public setMode(mode: QueueMode): void {
        if (mode === 'random' || mode === 'sequential') {
            this.mode = mode;
        }
    }

    /**
     * 履歴をクリア
     */
    public clearHistory(): void {
        this.history = [];
    }

    /**
     * 統計情報を取得
     * @returns Queue statistics
     */
    public getStats(): QueueStats {
        const playCount: Record<string, number> = {};
        this.history.forEach(item => {
            playCount[item.src] = (playCount[item.src] || 0) + 1;
        });

        return {
            totalPlays: this.history.length,
            uniquePlays: Object.keys(playCount).length,
            mostPlayed: Object.entries(playCount)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([src, count]) => ({ src, count }))
        };
    }

    /**
     * ステータスを取得
     * @returns Queue status information
     */
    public getStatus(): QueueStatus {
        return {
            totalVideos: this.videos.length,
            categoryCounts: {
                all: this.categorizedVideos.all.length,
                flat: this.categorizedVideos.flat.length,
                depth: this.categorizedVideos.depth.length
            },
            mode: this.mode,
            historySize: this.history.length,
            stats: this.getStats()
        };
    }

    /**
     * カタログから動画リストをロード
     * @param catalogUrl - URL to unified asset catalog
     * @returns Success status
     */
    public async loadFromCatalog(catalogUrl = '/assets/unified-asset-catalog.json'): Promise<boolean> {
        try {
            let catalog: UnifiedAssetCatalog | null = (getUnifiedCatalogCache() as unknown as UnifiedAssetCatalog) || null;

            if (!catalog && typeof window.getUnifiedAssetCatalog === 'function' && catalogUrl === '/assets/unified-asset-catalog.json') {
                catalog = await window.getUnifiedAssetCatalog();
            }

            if (!catalog && catalogUrl !== '/assets/unified-asset-catalog.json') {
                const response = await fetch(catalogUrl);
                catalog = await response.json();
            }

            if (!catalog) {
                throw new Error('Unified catalog not available');
            }

            const videos: VideoData[] = [];

            // フラット背景
            if (catalog.backgrounds?.flat_backgrounds?.assets) {
                const basePath = catalog.backgrounds.flat_backgrounds.basePath || '/assets/backgrounds/flat/';
                catalog.backgrounds.flat_backgrounds.assets.forEach(asset => {
                    videos.push({
                        src: basePath + asset.file,
                        name: asset.name || asset.file,
                        category: 'flat',
                        metadata: asset
                    });
                });
            }

            // 深度付き背景
            if (catalog.backgrounds?.depth_backgrounds?.assets) {
                const basePath = catalog.backgrounds.depth_backgrounds.basePath || '/assets/backgrounds/depth/';
                catalog.backgrounds.depth_backgrounds.assets.forEach(asset => {
                    videos.push({
                        src: basePath + asset.file,
                        name: asset.name || asset.file,
                        category: 'depth',
                        metadata: asset
                    });
                });
            }

            this.updateVideos(videos);

            // ログ出力で読み込み状況を確認
            logger.info(`📊 VideoQueue loaded ${videos.length} videos from catalog`);
            if (videos.length > 0) {
                const _categoryCounts = {
                    flat: videos.filter(v => v.category === 'flat').length,
                    depth: videos.filter(v => v.category === 'depth').length
                };
            }

            return true;

        } catch (error) {
            logger.error('❌ Failed to load video catalog:', error);
            return false;
        }
    }

    /**
     * Get current video list
     * @returns Array of videos
     */
    public getVideos(): VideoData[] {
        return [...this.videos];
    }

    /**
     * Get history
     * @returns Array of history entries
     */
    public getHistory(): HistoryEntry[] {
        return [...this.history];
    }

    /**
     * Get current mode
     * @returns Current queue mode
     */
    public getMode(): QueueMode {
        return this.mode;
    }

    /**
     * Check if queue is empty
     * @returns True if queue has no videos
     */
    public isEmpty(): boolean {
        return this.videos.length === 0;
    }

    /**
     * Get queue size
     * @returns Number of videos in queue
     */
    public size(): number {
        return this.videos.length;
    }
}

// ============================================================================
// Exports
// ============================================================================

// CommonJS export
if (typeof module !== 'undefined' && module.exports) {
    module.exports = VideoQueue;
}

// ES Module default export
export default VideoQueue;
